import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { BRAND, BRAND_DARK, BRAND_LIGHT } from "@/shared/constants.js";
import { formatMoneyOrDash } from "@/shared/utils/format.js";
import { Field } from "@/shared/components/Field.jsx";
import { PaymentCardField } from "@/modules/Payment/components/PaymentCardField.jsx";
import { QuoteStrip } from "@/modules/Quote/components/QuoteStrip.jsx";
import { SectionTitle } from "@/shared/components/SectionTitle.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { LockIcon } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import Loader from "@/shared/components/Loader.jsx";
import { btnPrimary, dis, inputBase } from "@/shared/utils/styles.js";
import { fmtDate } from "@/shared/utils/dateHelpers.js";
import { toError } from "@/shared/utils/misc.js";
import { isPaymentStatusSuccess, paymentFailureMessage } from "@/modules/Payment/utils/payment.js";

import {
  fetchPaymentPublishableKey,
  postPaymentConfirm,
  postPaymentInitiate,
} from "@/modules/Payment/api/paymentApi.js";
import { PaymentRequest } from "@/shared/dtos/index.js";
import { refreshPaymentOrder } from "@/modules/Payment/services/paymentOrderService.js";
import { readPolicyStatus, POLICY_STATUS } from "@/modules/Payment/utils/policyStatus.js";

import { useStore } from "@/shared/store/useStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import bindStore from "@/modules/Payment/store/bindStore.js";
import stripeStore from "@/modules/Payment/store/stripeStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import {
  classificationFromSubmissionGroups,
  fetchSubmissionQuestions,
  priorActsFromSubmissionGroups,
  underwritingAnyYesFromSubmissionGroups,
} from "@/modules/Quote/api/questionsApi.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";

export default function PaymentPage() {
  const navigate = useNavigate();
  const paymentCardRef = useRef(null);
  const stripeKeyRequestedRef = useRef(false);
  // Synchronous double-submit lock. `bindLoading` is a React state and
  // doesn't flip until after the current event loop tick — two rapid
  // clicks on "Pay" can both pass the `bindLoading` guard. A ref flips
  // immediately, so the second click bails before any Stripe call fires.
  const payInProgressRef = useRef(false);

  // Card form completeness — only this page cares.
  const [stripeCardComplete, setStripeCardComplete] = useState(false);
  const [stripeCardError, setStripeCardError] = useState(null);

  // ── Store reads ─────────────────────────────────────────────────────────
  const paymentOrderLoading = useStore(paymentOrderStore, (s) => s.paymentOrderLoading);
  const paymentOrderError   = useStore(paymentOrderStore, (s) => s.paymentOrderError);
  const paymentOrderDetails = useStore(paymentOrderStore, (s) => s.paymentOrderDetails);
  const stripeKeyLoading    = useStore(stripeStore, (s) => s.stripeKeyLoading);
  const stripeKeyError      = useStore(stripeStore, (s) => s.stripeKeyError);
  const stripePromise       = useStore(stripeStore, (s) => s.stripePromise);
  const bindLoading         = useStore(bindStore, (s) => s.bindLoading);
  const bindError           = useStore(bindStore, (s) => s.bindError);
  const priorActs           = useStore(coverageStore, (s) => s.priorActs);
  const retroDate           = useStore(coverageStore, (s) => s.retroDate);
  const flowSubmissionId    = useStore(submissionStore, (s) => s.flowSubmissionId);
  const submissionQuestionGroups = useStore(questionsStore, (s) => s.submissionQuestionGroups);
  const submissionQuestionsSid   = useStore(questionsStore, (s) => s.submissionQuestionsSid);

  const {
    quoteStripAnnualTotal, snapshotLimits, snapshotPolicyLine, quoteStripAmountPending,
    reviewApplicantName, reviewApplicantEmail, reviewApplicantPhone, reviewApplicantAddr,
    reviewPolicyPeriod, reviewLimitsLabel, reviewStateLabel, actualClass,
    anyYes, paymentBillingDetails, premium, total, paymentAmountDue,
  } = useQuoteSnapshot();

  // ── Load Stripe.js once on this page ───────────────────────────────────
  useEffect(() => {
    if (stripePromise || stripeKeyRequestedRef.current) return undefined;
    stripeKeyRequestedRef.current = true;
    const ctrl = new AbortController();
    stripeStore.stripeKeyLoading = true;
    stripeStore.stripeKeyError = null;
    (async () => {
      try {
        const stripeKey = await fetchPaymentPublishableKey({ signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        stripeStore.stripePromise = loadStripe(stripeKey.publishableKey);
      } catch (e) {
        if (ctrl.signal.aborted || e?.name === "AbortError") return;
        stripeStore.stripeKeyError = toError(e).message;
        stripeKeyRequestedRef.current = false;
      } finally {
        if (!ctrl.signal.aborted) stripeStore.stripeKeyLoading = false;
      }
    })();
    return () => {
      ctrl.abort();
      if (!stripeStore.stripePromise) stripeKeyRequestedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load submission questions for the summary ──────────────────────────
  // Classification / Prior acts / Underwriting in the summary are read from
  // the submission question tree (each option carries its saved answerValue).
  // The bootstrap fetch skips /payment, so when the user lands here directly
  // (Resume, refresh) the tree is empty — pull it once for the active sid.
  useEffect(() => {
    const sidNum = Number(flowSubmissionId);
    if (!Number.isFinite(sidNum) || sidNum <= 0) return undefined;
    const cached = questionsStore.submissionQuestionGroups;
    // Only trust the cache when it belongs to this submission — resuming a
    // different sid leaves the prior tree in the store, which would otherwise
    // show the previous submission's classification / prior-acts / answers.
    const hasCached = Array.isArray(cached) && cached.length > 0
      && questionsStore.submissionQuestionsSid === String(flowSubmissionId);
    if (hasCached) return undefined;
    const ctrl = new AbortController();
    questionsStore.submissionQuestionsLoading = true;
    questionsStore.submissionQuestionsError = null;
    fetchSubmissionQuestions(flowSubmissionId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        questionsStore.submissionQuestionGroups = Array.isArray(data) ? data : [];
        questionsStore.submissionQuestionsSid = flowSubmissionId;
      })
      .catch((e) => {
        if (ctrl.signal.aborted || e?.name === "AbortError") return;
        questionsStore.submissionQuestionsError = e;
      })
      .finally(() => {
        if (!ctrl.signal.aborted) questionsStore.submissionQuestionsLoading = false;
      });
    return () => { ctrl.abort(); };
  }, [flowSubmissionId]);

  // ── Pay & bind ─────────────────────────────────────────────────────────
  const handlePayAndBind = async () => {
    if (payInProgressRef.current) return;
    if (bindLoading) return;
    if (!canBind) return;
    if (!flowSubmissionId) {
      bindStore.bindError = "Missing submission id. Please resume your application and try again.";
      return;
    }
    if (!paymentCardRef.current) {
      bindStore.bindError = "Payment form is still loading. Please try again in a moment.";
      return;
    }
    if (!paymentAmountDue || paymentAmountDue <= 0) {
      bindStore.bindError = "Payment amount is unavailable. Please refresh the quote and try again.";
      return;
    }
    // Lock BEFORE any await so a second click in the same tick bails.
    payInProgressRef.current = true;
    bindStore.bindError = null;
    bindStore.bindLoading = true;
    try {
      const submissionId = Number(flowSubmissionId);
      const paymentMethod = await paymentCardRef.current.createPaymentMethod();
      const initiatedPayment = await postPaymentInitiate(new PaymentRequest({
        paymentId: paymentMethod.id, submissionId,
      }));

      let paymentIntentId = initiatedPayment?.paymentIntentId;
      let paymentStatus = initiatedPayment?.status;
      if (!paymentIntentId) throw new Error("Payment processor did not return a payment intent.");

      if (!isPaymentStatusSuccess(paymentStatus) && initiatedPayment?.clientSecret) {
        const paymentIntent = await paymentCardRef.current.confirmCardPayment(initiatedPayment.clientSecret);
        paymentIntentId = paymentIntent?.id || paymentIntentId;
        paymentStatus = paymentIntent?.status || paymentStatus;
      }

      const confirmedPayment = await postPaymentConfirm(new PaymentRequest({ submissionId, paymentId: paymentIntentId }));
      if (!isPaymentStatusSuccess(confirmedPayment?.status || paymentStatus)) {
        throw new Error(paymentFailureMessage(confirmedPayment));
      }

      // Payment confirmed at Stripe — pull the authoritative order from
      // /insured/order so the policyStatus the backend just flipped is in
      // the store before BinderInvoicePage mounts (its mount effect kicks
      // the user back to /dashboard when policyStatus !== 'PAID').
      const refreshedOrder = await refreshPaymentOrder(submissionId);
      if (readPolicyStatus(refreshedOrder) !== POLICY_STATUS.PAID) {
        throw new Error("Payment processed but order status hasn't updated yet. Please refresh in a moment.");
      }

      const r = refreshedOrder?.ratingResponse ?? paymentOrderDetails?.ratingResponse;
      const apiTotal = r?.total != null && Number.isFinite(Number(r.total)) ? Math.round(Number(r.total)) : null;
      bindStore.boundTotalAmount = apiTotal != null ? apiTotal : total;

      navigate("/binder-invoice");
    } catch (e) {
      bindStore.bindError = toError(e).message;
    } finally {
      bindStore.bindLoading = false;
      payInProgressRef.current = false;
    }
  };

  const canBind = Boolean(stripePromise)
    && stripeCardComplete
    && !stripeCardError
    && !stripeKeyError
    && !bindLoading;

  // ── Summary fields sourced from the submission questions ───────────────
  // Prefer the answers baked into the submission tree (authoritative for the
  // submission, available even after a Resume); fall back to the in-memory
  // wizard stores while the tree is still loading.
  const od = paymentOrderDetails;
  const submissionNumberDisplay =
    (od?.policynumber && String(od.policynumber).trim())
    || (od?.quotenumber && String(od.quotenumber).trim())
    || (flowSubmissionId ? `#${flowSubmissionId}` : null);

  // Only read answers from the tree once it's confirmed to belong to the
  // active submission — otherwise a just-resumed submission would briefly
  // show the previously-loaded submission's answers.
  const questionGroupsForSubmission =
    submissionQuestionsSid === String(flowSubmissionId) ? submissionQuestionGroups : [];

  const classFromQ = classificationFromSubmissionGroups(questionGroupsForSubmission);
  const resolvedClass = classFromQ ?? actualClass;
  const classificationLabel = resolvedClass === "PA2"
    ? "PA 2 — Obstetrical"
    : "PA 1 — Non-obstetrical";

  const priorActsFromQ = priorActsFromSubmissionGroups(questionGroupsForSubmission);
  const ratingRetro = od?.ratingResponse?.retroCoverageDate ?? od?.ratingResponse?.retrocoveragedate;
  const priorActsLabel = (() => {
    const isYes = priorActsFromQ ? priorActsFromQ.isYes : priorActs === "yes";
    if (!isYes) return "Waived";
    const retro = (priorActsFromQ && priorActsFromQ.retro) || ratingRetro || retroDate;
    return `Yes — retro ${fmtDate(retro) || "pending"}`;
  })();

  const underwritingFromQ = underwritingAnyYesFromSubmissionGroups(questionGroupsForSubmission);
  const underwritingAnyYes = underwritingFromQ ?? anyYes;
  const underwritingLabel = underwritingAnyYes ? "Yes responses — referral" : "All clear";

  return (
    <>
      <QuoteStrip total={quoteStripAnnualTotal} limits={snapshotLimits} claims={snapshotPolicyLine} amountPending={quoteStripAmountPending} amountLabel="Your total" />
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 600, color: "#1a1a1a", margin: "6px 0 4px" }}>Payment</h2>
      <p style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.5 }}>Secure payment via Stripe. We never store card details.</p>

      {paymentOrderLoading && <Loader label="Loading submission…" />}
      {flowSubmissionId != null && <Alert type="error" message={paymentOrderError} />}

      <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 8 }}>Submission summary</div>
        {[
          submissionNumberDisplay && ["Submission #", submissionNumberDisplay],
          ["Applicant", reviewApplicantName],
          ["Email", reviewApplicantEmail],
          ["Phone", reviewApplicantPhone],
          reviewApplicantAddr && ["Address", reviewApplicantAddr],
          ["Policy period", reviewPolicyPeriod],
          ["Limits", reviewLimitsLabel],
          ["State", reviewStateLabel],
          ["Classification", classificationLabel],
          ["Prior acts", priorActsLabel],
          ["Underwriting", underwritingLabel],
        ].filter(Boolean).map(([l, v]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 11.5, color: "#888" }}>{l}</span>
            <span style={{ fontSize: 11.5, color: "#444", fontWeight: 500, textAlign: "right", maxWidth: "60%" }}>{v}</span>
          </div>
        ))}
      </div>

      {stripeKeyLoading && <Loader label="Loading secure payment form…" />}
      <Alert type="error" message={stripeKeyError} />
      <Field label="Credit/Debit card payment">
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <PaymentCardField
              ref={paymentCardRef}
              disabled={bindLoading}
              billingDetails={paymentBillingDetails}
              onCompleteChange={setStripeCardComplete}
              onErrorChange={setStripeCardError}
            />
          </Elements>
        ) : (
          <div style={{ ...inputBase, padding: "13px 14px", color: "#999" }}>
            {stripeKeyError ? "Payment form unavailable" : "Loading Stripe…"}
          </div>
        )}
      </Field>
      <Alert type="error" message={stripeCardError} />
      <div style={{ background: "#f7f7f5", borderRadius: 12, padding: "14px 16px", marginTop: 8 }}>
        <SectionTitle>Order summary</SectionTitle>
        {(() => {
          const r = paymentOrderDetails?.ratingResponse;
          const hasApi = r && (r.premium != null || r.tax != null || r.fees != null || r.total != null);
          if (hasApi) {
            const rows = [];
            if (r.premium != null) rows.push(["Premium", formatMoneyOrDash(r.premium)]);
            if (r.tax != null) rows.push(["Taxes", formatMoneyOrDash(r.tax)]);
            if (r.fees != null) rows.push(["Fees", formatMoneyOrDash(r.fees)]);
            if (rows.length === 0 && r.total != null) rows.push(["Quoted total", formatMoneyOrDash(r.total)]);
            return rows;
          }
          const rows = [["Estimated premium", premium > 0 ? `$${premium.toLocaleString()}` : "—"]];
          rows.push(["Taxes", "—"], ["Fees", "—"]);
          return rows;
        })().map(([l, v]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: "#777" }}>{l}</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#333" }}>{v}</span>
          </div>
        ))}
        <div style={{ height: 1, background: "#e0e0de", margin: "8px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>Total due today</span>
          <span style={{ fontSize: 16, fontWeight: 500, color: BRAND_DARK }}>
            {(() => {
              const r = paymentOrderDetails?.ratingResponse;
              if (r?.total != null && Number.isFinite(Number(r.total))) return `$${Math.round(Number(r.total)).toLocaleString()}`;
              return total > 0 ? `$${total.toLocaleString()}` : "—";
            })()}
          </span>
        </div>
      </div>
      <div style={{ background: BRAND_LIGHT, borderRadius: 10, padding: "10px 14px", marginTop: 10, display: "flex", alignItems: "flex-start", gap: 8 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2" strokeLinecap="round" style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span style={{ fontSize: 11, color: BRAND_DARK, lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>
          <strong>Cancellation rights:</strong> You may cancel your policy before your policy effective date for a full refund of all premiums, fees, and contributions.
        </span>
      </div>
      <Spacer />
      <Alert type="error" message={bindError} className="alert-center" />

      <button style={dis({ ...btnPrimary, fontSize: 15 }, canBind)} onClick={handlePayAndBind}>
        <LockIcon /> {bindLoading ? "Processing…" : `Pay ${(() => {
          const r = paymentOrderDetails?.ratingResponse;
          if (r?.total != null && Number.isFinite(Number(r.total))) return `$${Math.round(Number(r.total)).toLocaleString()}`;
          return total > 0 ? `$${total.toLocaleString()}` : "—";
        })()}`}
      </button>
      <p style={{ fontSize: 10, color: "#bbb", textAlign: "center", marginTop: 8 }}>256-bit SSL encrypted · PCI-DSS compliant</p>
    </>
  );
}
